import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users,
  Building2,
  DollarSign,
  Calendar,
  Loader2,
  RefreshCw,
  Sun,
  Moon,
  LogOut,
  ArrowUpRight,
  ArrowDownRight,
  Coins,
  Activity,
  Briefcase,
  MapPin,
  Clock,
  IndianRupee
} from 'lucide-react';
import { ProfileModal } from '../components/ProfileModal';

const LOGO_URL = '/logo.webp';

const FoundersDashboard = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    fetchData();
    // Auto-refresh realtime data every 30 seconds
    const interval = setInterval(() => {
      if (activeTab === 'realtime') {
        fetchRealtime();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [api, activeTab]);

  const fetchData = async () => {
    try {
      const [overviewRes, realtimeRes] = await Promise.all([
        api.get('/founders/overview'),
        api.get('/founders/real-time')
      ]);
      setOverview(overviewRes.data);
      setRealtime(realtimeRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtime = async () => {
    try {
      const res = await api.get('/founders/real-time');
      setRealtime(res.data);
    } catch (error) {
      console.error('Error fetching realtime data:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toLocaleString() || 0}`;
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'growth', label: 'Growth & Trends', icon: TrendingUp },
    { id: 'realtime', label: 'Real-time', icon: Activity },
  ];

  // Theme-aware colors
  const sidebarBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const sidebarBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const cardBg = isDark ? 'bg-[#13161a]' : 'bg-white';
  const cardBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const innerCardBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const hoverBg = isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="founders-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`hidden lg:flex w-64 flex-col fixed inset-y-0 z-50 ${sidebarBg} border-r ${sidebarBorder}`}>
          <div className={`p-6 border-b ${sidebarBorder}`}>
            <Link to="/founders" className="flex flex-col gap-1.5">
              <img src={LOGO_URL} alt="Clayworks" className="h-7 w-auto" />
              <p className="text-xs font-medium text-muted-foreground tracking-wide">Founders View</p>
            </Link>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {sidebarItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'bg-primary text-primary-foreground' 
                    : `text-muted-foreground hover:text-foreground ${hoverBg}`
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className={`p-4 border-t ${sidebarBorder}`}>
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground ${hoverBg} mb-2`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProfileModal(true)}
                className={`flex items-center gap-2 flex-1 min-w-0 p-2 rounded-xl ${cardBg} hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer text-left`}
                data-testid="sidebar-profile-btn"
              >
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive shrink-0 px-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </aside>

        <ProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          api={api}
          onProfileUpdate={() => window.location.reload()}
        />

        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                  {activeTab === 'overview' ? 'Executive Overview' : sidebarItems.find(i => i.id === activeTab)?.label}
                </h1>
                <p className="text-muted-foreground mt-1">Strategic insights at a glance</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className={`${cardBorder} ${hoverBg}`} onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Badge variant="outline" className="text-xs py-2">
                  <Activity className="w-3 h-3 mr-1" /> Read Only
                </Badge>
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && overview && (
              <div className="space-y-8">
                {/* Hero KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-emerald-600 dark:text-emerald-400">Total Revenue</p>
                          <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(overview.kpis.total_revenue)}</p>
                          <div className="flex items-center text-xs mt-2">
                            {overview.this_month.revenue_growth >= 0 ? (
                              <><ArrowUpRight className="w-3 h-3 mr-1 text-emerald-500" /><span className="text-emerald-500">+{overview.this_month.revenue_growth}%</span></>
                            ) : (
                              <><ArrowDownRight className="w-3 h-3 mr-1 text-red-500" /><span className="text-red-500">{overview.this_month.revenue_growth}%</span></>
                            )}
                            <span className="text-muted-foreground ml-1">vs last month</span>
                          </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <IndianRupee className="w-6 h-6 text-emerald-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Bookings</p>
                          <p className="text-3xl font-heading font-bold mt-2">{overview.kpis.total_bookings.toLocaleString()}</p>
                          <div className="flex items-center text-xs mt-2">
                            {overview.this_month.booking_growth >= 0 ? (
                              <><ArrowUpRight className="w-3 h-3 mr-1 text-emerald-500" /><span className="text-emerald-500">+{overview.this_month.booking_growth}%</span></>
                            ) : (
                              <><ArrowDownRight className="w-3 h-3 mr-1 text-red-500" /><span className="text-red-500">{overview.this_month.booking_growth}%</span></>
                            )}
                            <span className="text-muted-foreground ml-1">growth</span>
                          </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-blue-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Users</p>
                          <p className="text-3xl font-heading font-bold mt-2">{overview.kpis.total_users.toLocaleString()}</p>
                          <p className="text-xs text-emerald-500 mt-2">
                            +{overview.this_month.new_users} this month
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Organizations</p>
                          <p className="text-3xl font-heading font-bold mt-2">{overview.kpis.total_organizations}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            B2B accounts
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-amber-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Secondary Metrics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading font-bold">{overview.kpis.active_venues}</p>
                          <p className="text-xs text-muted-foreground">Active Venues</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading font-bold">{overview.kpis.total_bookables}</p>
                          <p className="text-xs text-muted-foreground">Bookable Spaces</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <Coins className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading font-bold">{overview.kpis.total_credits_in_system?.toFixed(0) || 0}</p>
                          <p className="text-xs text-muted-foreground">Credits in System</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading font-bold text-primary">{formatCurrency(overview.this_month.revenue)}</p>
                          <p className="text-xs text-primary/70">This Month Revenue</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue by Region */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Revenue by Region
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overview.revenue_by_region?.map((region, idx) => {
                        const maxRevenue = Math.max(...overview.revenue_by_region.map(r => r.revenue));
                        const percentage = maxRevenue > 0 ? (region.revenue / maxRevenue) * 100 : 0;
                        return (
                          <div key={region.region} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {idx === 0 && <Badge className="bg-amber-500/20 text-amber-500 text-xs">Top</Badge>}
                                <span className="font-medium">{region.region}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-heading font-semibold">{formatCurrency(region.revenue)}</span>
                                <span className="text-xs text-muted-foreground ml-2">({region.bookings} bookings)</span>
                              </div>
                            </div>
                            <div className={`h-2 ${innerCardBg} rounded-full overflow-hidden`}>
                              <div 
                                className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {(!overview.revenue_by_region || overview.revenue_by_region.length === 0) && (
                        <p className="text-center text-muted-foreground py-4">No revenue data yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Growth & Trends Tab */}
            {activeTab === 'growth' && overview && (
              <div className="space-y-8">
                {/* Monthly Comparison */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardHeader>
                      <CardTitle className="font-heading">This Month vs Last Month</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">Revenue</span>
                            <Badge className={overview.this_month.revenue_growth >= 0 ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}>
                              {overview.this_month.revenue_growth >= 0 ? '+' : ''}{overview.this_month.revenue_growth}%
                            </Badge>
                          </div>
                          <div className="flex items-end gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">This Month</p>
                              <p className="text-2xl font-heading font-bold">{formatCurrency(overview.this_month.revenue)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Last Month</p>
                              <p className="text-lg text-muted-foreground">{formatCurrency(overview.comparisons.prev_month_revenue)}</p>
                            </div>
                          </div>
                        </div>

                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">Bookings</span>
                            <Badge className={overview.this_month.booking_growth >= 0 ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}>
                              {overview.this_month.booking_growth >= 0 ? '+' : ''}{overview.this_month.booking_growth}%
                            </Badge>
                          </div>
                          <div className="flex items-end gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">This Month</p>
                              <p className="text-2xl font-heading font-bold">{overview.this_month.bookings}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Last Month</p>
                              <p className="text-lg text-muted-foreground">{overview.comparisons.prev_month_bookings}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Trend Chart (simplified) */}
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardHeader>
                      <CardTitle className="font-heading">6-Month Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {overview.monthly_trend?.map((month, idx) => {
                          const maxRevenue = Math.max(...overview.monthly_trend.map(m => m.revenue || 0));
                          const percentage = maxRevenue > 0 ? ((month.revenue || 0) / maxRevenue) * 100 : 0;
                          return (
                            <div key={month.month} className="flex items-center gap-3">
                              <span className="w-16 text-xs text-muted-foreground">{month.month}</span>
                              <div className={`flex-1 h-6 ${innerCardBg} rounded-full overflow-hidden relative`}>
                                <div 
                                  className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full transition-all duration-500"
                                  style={{ width: `${percentage}%` }}
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium">
                                  {formatCurrency(month.revenue || 0)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {(!overview.monthly_trend || overview.monthly_trend.length === 0) && (
                          <p className="text-center text-muted-foreground py-4">No trend data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Real-time Tab */}
            {activeTab === 'realtime' && realtime && (
              <div className="space-y-8">
                {/* Live Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <Activity className="w-6 h-6 text-emerald-500 animate-pulse" />
                        </div>
                        <div>
                          <p className="text-3xl font-heading font-bold text-emerald-500">{realtime.active_guests_now}</p>
                          <p className="text-sm text-emerald-600 dark:text-emerald-400">Active Right Now</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-3xl font-heading font-bold">{realtime.upcoming_next_3hrs}</p>
                          <p className="text-sm text-muted-foreground">Upcoming (3hrs)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-3xl font-heading font-bold">{realtime.today_total_bookings}</p>
                          <p className="text-sm text-muted-foreground">Today's Total</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <IndianRupee className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-3xl font-heading font-bold">{formatCurrency(overview?.today?.revenue || 0)}</p>
                          <p className="text-sm text-muted-foreground">Today's Revenue</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Transactions */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Coins className="w-5 h-5 text-primary" />
                      Recent Credit Transactions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {realtime.recent_transactions?.slice(0, 8).map((trans, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-xl ${innerCardBg}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${trans.amount > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                              {trans.amount > 0 ? (
                                <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{trans.description?.slice(0, 40) || trans.transaction_type}</p>
                              <p className="text-xs text-muted-foreground">{trans.created_at?.split('T')[0]}</p>
                            </div>
                          </div>
                          <span className={`font-heading font-semibold ${trans.amount > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {trans.amount > 0 ? '+' : ''}{trans.amount?.toFixed(1)} credits
                          </span>
                        </div>
                      ))}
                      {(!realtime.recent_transactions || realtime.recent_transactions.length === 0) && (
                        <p className="text-center text-muted-foreground py-4">No recent transactions</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default FoundersDashboard;
